import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Shield, Users, Plus, Edit, Trash2, Search, AlertCircle, Copy, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDataSource } from '@/contexts/DataSourceContext';
import { getUserRoleService, type UserRoleListItem } from '@/services/userRoleService';

interface RolesPermissionsPageProps {
  onNavigate?: (path: string) => void;
}

export const RolesPermissionsPage: React.FC<RolesPermissionsPageProps> = ({ onNavigate }) => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('roles-permissions');
  const userRoleService = useMemo(() => getUserRoleService(screenMode), [screenMode]);

  const [searchQuery, setSearchQuery] = useState('');
  const [roles, setRoles] = useState<UserRoleListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [roleToDuplicate, setRoleToDuplicate] = useState<UserRoleListItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<UserRoleListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load roles from API
  const loadRoles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await userRoleService.getAllUserRoles();
      setRoles(data);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [userRoleService]);

  // Load roles on mount and when service changes
  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  // Listen for role updates (for demo mode localStorage sync)
  useEffect(() => {
    const handleStorageChange = () => {
      if (screenMode === 'demo') {
        loadRoles();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('rolesUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('rolesUpdated', handleStorageChange);
    };
  }, [screenMode, loadRoles]);

  const filteredRoles = roles.filter((role) =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (role.description?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  const handleEditRole = (roleId: number) => {
    onNavigate?.(`/settings/roles-permissions/edit/${roleId}`);
  };

  const handleCreateRole = () => {
    onNavigate?.(`/settings/roles-permissions/create`);
  };

  const handleDuplicateRole = (role: UserRoleListItem) => {
    setRoleToDuplicate(role);
    setDuplicateDialogOpen(true);
  };

  const handleDeleteRole = (role: UserRoleListItem) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;

    setIsDeleting(true);
    try {
      await userRoleService.deleteUserRole(roleToDelete.id);
      await loadRoles();
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      // Dispatch event for other components
      window.dispatchEvent(new Event('rolesUpdated'));
    } catch (err) {
      console.error('Error deleting role:', err);
      setError('Failed to delete role. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleConfirmDuplicate = () => {
    if (!roleToDuplicate) return;

    try {
      // Store duplication data in sessionStorage for the EditRolePage to use
      const duplicationData = {
        sourceRoleId: roleToDuplicate.id,
        sourceRoleName: roleToDuplicate.name,
        sourceRoleDescription: roleToDuplicate.description,
        timestamp: new Date().toISOString(),
      };
      
      sessionStorage.setItem('roleDuplicationData', JSON.stringify(duplicationData));

      // Close dialog
      handleCancelDuplicate();

      // Navigate to create page (which will detect duplication data and load permissions)
      if (onNavigate) {
        onNavigate('/settings/roles-permissions/create');
      }
    } catch (error) {
      console.error('Error initiating role duplication:', error);
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
    setRoleToDuplicate(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
          title="Roles & Permissions"

          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Shield />}
        />
        <Button onClick={handleCreateRole}>
          <Plus className="h-4 w-4 mr-2" />
          Create Role
        </Button>
      </div>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>User Roles</CardTitle>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Users</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading roles...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-destructive">
                      <AlertCircle className="h-5 w-5" />
                      <span>{error}</span>
                      <Button variant="outline" size="sm" onClick={loadRoles}>
                        Try Again
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredRoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No roles found
                  </TableCell>
                </TableRow>
              ) : filteredRoles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell className="font-medium">
                    {role.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {role.description}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline">{role.userCount}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge 
                      variant={role.isActive ? "default" : "secondary"}
                      className={role.isActive ? "bg-success-500 hover:bg-success-600" : ""}
                    >
                      {role.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="text-xs">
                      {role.isSystem ? 'Platform' : 'Custom'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEditRole(role.id)}
                        title="Edit role permissions"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDuplicateRole(role)}
                        title="Duplicate role"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        disabled={role.isSystem}
                        onClick={() => handleDeleteRole(role)}
                        title={role.isSystem ? 'Platform roles cannot be deleted' : 'Delete role'}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Role Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Roles</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{roles.length}</div>
            <p className="text-xs text-muted-foreground">
              Active role definitions
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {roles.reduce((sum, role) => sum + role.userCount, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Users with assigned roles
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Platform Roles</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {roles.filter((role) => role.isSystem).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Default system roles
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Duplicate Role Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Duplicate Role</DialogTitle>
            <DialogDescription>
              Create a new role based on "{roleToDuplicate?.name}". You'll be able to configure the name, description, and permissions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Source Role Info */}
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="flex items-start gap-3">
                <Copy className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium">Duplicating from:</p>
                  <p className="text-sm font-semibold">{roleToDuplicate?.name}</p>
                  <p className="text-xs text-muted-foreground">{roleToDuplicate?.description}</p>
                </div>
              </div>
            </div>

            {/* What will be copied */}
            <div className="rounded-lg border bg-info-50 dark:bg-info-950/20 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-info-600 dark:text-info-400 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-info-900 dark:text-info-100">
                    What will be copied:
                  </p>
                  <ul className="text-xs text-info-700 dark:text-info-300 space-y-1 list-disc list-inside">
                    <li>All permission settings and access levels</li>
                    <li>Time tracking configurations</li>
                    <li>Approval and report permissions</li>
                    <li>Management and integration settings</li>
                  </ul>
                  <p className="text-xs text-info-700 dark:text-info-300 mt-2">
                    You'll be able to modify everything before saving the new role.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDuplicate}>
              Cancel
            </Button>
            <Button onClick={handleConfirmDuplicate}>
              Continue to Configure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role "{roleToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {roleToDelete && roleToDelete.userCount > 0 && (
            <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/20 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    Warning
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    This role has {roleToDelete.userCount} user(s) assigned. They will need to be reassigned to another role.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
